/**
 * Export flow hook — owns all async export flows, progress tracking,
 * and toast dispatch. Extracted from ChatManagementModal to keep the component
 * focused on layout and lifecycle.
 */
import { useCallback, useRef } from 'react';
import { useChatManagementStore } from '../stores/chatManagementStore';
import { useToastStore } from '../stores/ToastStore';
import { IExportService } from '../types/exportTypes';
import { ToastType } from '../types/ui';
import { apiClient } from '../clients/APIClient';
import { buildExportOptions, wrapPreviewHtml } from '../utils/chatExportUtils';
import {
	buildBulkExportStatusMessage,
	classifyBulkExportResult,
} from '../components/chat-management/CMShared';
import { useChatListStore } from '../stores/chatListStore';
import { useChatHistoryStore } from '../stores/chatHistoryStore';
import { useChatUIStore } from '../stores/chatUIStore';
import { useUserSettings } from '../stores/settingsRegistry';
import { downloadMagazine, generateMagazineFilename } from '../utils/magazineExport';

export function useExportFlows(exportService: IExportService) {
	const { actions } = useChatManagementStore();
	const exportState = useChatManagementStore((s) => s.export);
	const selectionState = useChatManagementStore((s) => s.selection);
	const { showToast } = useToastStore();
	const chats = useChatListStore((s) => s.chats);
	const selectedModel = useChatHistoryStore((s) => s.selectedModel);
	const setLastExportFilePath = useChatUIStore((s) => s.setLastExportFilePath);
	const setLastExportDirectory = useChatUIStore((s) => s.setLastExportDirectory);
	const userSettings = useUserSettings();
	const defaultDirectory =
		(userSettings.helpers.getValue('export_options.defaultDirectory') as string) || undefined;

	// Ref prevents race conditions when preview requests overlap
	const previewReqIdRef = useRef(0);

	const generatePreview = useCallback(
		async (targetChatId: string) => {
			if (!targetChatId || exportState.mode === null) return;
			if (exportState.format === 'magazine') return;

			previewReqIdRef.current += 1;
			const reqId = previewReqIdRef.current;

			actions.setPreviewLoading(true);
			actions.setPreviewError('');

			try {
				const messages =
					await apiClient.history.getHistoryById(targetChatId);
				if (!Array.isArray(messages)) {
					throw new Error('No messages found for this chat');
				}

				const chatData = chats.find((c) => c.id === targetChatId);
				const exportOptions = buildExportOptions(
					exportState.format,
					exportState.theme,
					selectedModel || 'Unknown',
					targetChatId,
					exportState.config,
					chatData?.title,
				);

				const result = await exportService.generatePreview({
					messages,
					options: exportOptions,
				});

				if (reqId === previewReqIdRef.current) {
					const wrapped = wrapPreviewHtml(
						result.content,
						exportState.theme,
						exportState.format,
					);
					actions.setPreviewContent(wrapped);
					actions.setPreviewLoading(false);
				}
			} catch (error) {
				if (reqId === previewReqIdRef.current) {
					const rawMessage =
						error instanceof Error ? error.message : '';
					// Treat both "malformed content" and "empty history" as
					// format-incompatible, not as system errors.
					const isUnsupportedContent =
						rawMessage === 'No messages found for this chat' ||
						rawMessage.startsWith('Cannot read properties');
					const friendly = isUnsupportedContent
						? 'Chat appears malformed or has unsupported content. Preview unavailable for this format.'
						: rawMessage || 'Failed to generate preview';
					actions.setPreviewContent('');
					actions.setPreviewError(friendly);
					actions.setPreviewLoading(false);
				}
			}
		},
		[
			exportState.mode,
			exportState.format,
			exportState.theme,
			exportState.config,
			selectedModel,
			chats,
			exportService,
			actions,
		],
	);

	const handleExportSelected = useCallback(async () => {
		if (selectionState.selectedChats.length === 0) return;

		// Magazine export: separate code path
		if (exportState.format === 'magazine') {
			actions.setIsExporting(true);
			try {
				const result = await apiClient.files.exportMagazine({
					history_ids: selectionState.selectedChats,
					file_ids: [],
					collection_ids: [],
					encryption: exportState.encryptExport,
					signing: exportState.signExport,
					stream: true,
				});
				const filename = generateMagazineFilename('chats', {
					count: selectionState.selectedChats.length,
				});
				downloadMagazine(result instanceof Blob ? result : result.b64_mag, filename);
				showToast('Magazine exported successfully!', 'export_success', {
					timeout: 3000,
					placement: 'bottom-center',
					adaptToModal: true,
				});
			} catch (error) {
				const msg = error instanceof Error ? error.message : 'Export failed';
				showToast(msg, 'error', {
					timeout: 3000,
					placement: 'bottom-center',
					adaptToModal: true,
				});
			} finally {
				actions.setIsExporting(false);
			}
			return;
		}

		actions.setIsExporting(true);
		actions.setExportProgress(0, selectionState.selectedChats.length);

		try {
			const options = buildExportOptions(
				exportState.format,
				exportState.theme,
				selectedModel || 'Unknown',
				undefined,
				exportState.config,
			);

			const result = await exportService.exportBulk(
				{ chatIds: selectionState.selectedChats, options, defaultDirectory },
				(current, total) => {
					actions.setExportProgress(current, total);
				},
			);

			const resultClassification = classifyBulkExportResult(result);
			const statusMessage = buildBulkExportStatusMessage(result);

			const toastTypeMap: Record<typeof resultClassification, ToastType> =
				{
					complete_success: 'export_bulk_success',
					partial_success: 'export_warning',
					error: 'error',
				};

			if (
				resultClassification === 'complete_success' ||
				resultClassification === 'partial_success'
			) {
				setLastExportFilePath(null);
				setLastExportDirectory(result.targetDirectory || null);
			}

			showToast(statusMessage, toastTypeMap[resultClassification], {
				timeout: 5000,
				placement: 'bottom-center',
				adaptToModal: true,
			});
		} catch {
			showToast('Export failed. Please try again.', 'error', {
				timeout: 3000,
				placement: 'bottom-center',
				adaptToModal: true,
			});
		} finally {
			actions.setIsExporting(false);
		}
	}, [
		selectionState.selectedChats,
		exportState.format,
		exportState.theme,
		exportState.config,
		exportState.encryptExport,
		exportState.signExport,
		selectedModel,
		exportService,
		actions,
		showToast,
		setLastExportFilePath,
		setLastExportDirectory,
		defaultDirectory,
	]);

	const handleExportSingle = useCallback(
		async (targetChatId: string) => {
			// Magazine export: separate code path
			if (exportState.format === 'magazine') {
				actions.setIsExporting(true);
				try {
					const result = await apiClient.files.exportMagazine({
						history_ids: [targetChatId],
						file_ids: [],
						collection_ids: [],
						encryption: exportState.encryptExport,
						signing: exportState.signExport,
						stream: true,
					});
					const chatData = chats.find((c) => c.id === targetChatId);
					const prefix = chatData?.title?.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 40) || 'chat';
					const filename = generateMagazineFilename(prefix);
					downloadMagazine(result instanceof Blob ? result : result.b64_mag, filename);
					showToast('Magazine exported successfully!', 'export_success', {
						timeout: 3000,
						placement: 'bottom-center',
						adaptToModal: true,
					});
				} catch (error) {
					const msg = error instanceof Error ? error.message : 'Export failed';
					showToast(msg, 'error', {
						timeout: 3000,
						placement: 'bottom-center',
						adaptToModal: true,
					});
				} finally {
					actions.setIsExporting(false);
				}
				return;
			}

			actions.setIsExporting(true);

			try {
				const messages =
					await apiClient.history.getHistoryById(targetChatId);
				const chatData = chats.find((c) => c.id === targetChatId);
				const options = buildExportOptions(
					exportState.format,
					exportState.theme,
					selectedModel || 'Unknown',
					targetChatId,
					exportState.config,
					chatData?.title,
				);

				const result = await exportService.exportChat({
					chatId: targetChatId,
					messages,
					options,
					defaultDirectory,
				});

				actions.setExportMode(null);
				if (result.success) {
					setLastExportFilePath(result.filePath || null);
					setLastExportDirectory(null);
					showToast(result.successMessage || 'Chat exported successfully!', 'export_success', {
						timeout: 3000,
						placement: 'bottom-center',
						adaptToModal: true,
					});
				} else {
					showToast(result.error || 'Export failed. Please try again.', 'error', {
						timeout: 3000,
						placement: 'bottom-center',
						adaptToModal: true,
					});
				}
			} catch (error) {
				const errorMessage =
					error instanceof Error ? error.message : 'Export failed';
				actions.setExportMode(null);
				showToast(errorMessage, 'error', {
					timeout: 3000,
					placement: 'bottom-center',
					adaptToModal: true,
				});
			} finally {
				actions.setIsExporting(false);
			}
		},
		[
			exportState.format,
			exportState.theme,
			exportState.config,
			exportState.encryptExport,
			exportState.signExport,
			selectedModel,
			chats,
			exportService,
			actions,
			showToast,
			setLastExportFilePath,
			setLastExportDirectory,
			defaultDirectory,
		],
	);

	const handleCancelExport = useCallback(async () => {
		await exportService.cancelBulkExport();
		actions.setIsExporting(false);
		actions.setExportMode(null);
		showToast('Export cancelled', 'export_warning', {
			timeout: 2000,
			placement: 'bottom-center',
			adaptToModal: true,
		});
	}, [actions, exportService, showToast]);

	return {
		generatePreview,
		handleExportSelected,
		handleExportSingle,
		handleCancelExport,
	};
}
